/**
 * Policies — V98-3-1. Route: /w/:slug/app/policies (sidebar "Policies").
 *
 * The workspace's published policies with acknowledgment coverage, and the
 * two actions on them: publish a policy (owner/admin; members get AskAdmin)
 * and acknowledge the ones waiting for the viewer (`?acknowledge=1` opens
 * that drawer on arrival). Reuses the SOC 2 publish / acknowledge drawers.
 *
 * Not the parked v4 AI policy drafter (pages/Policies.tsx, routes
 * policies/new and policies/:draft_id stay parked — lib/parked.ts).
 *
 * V98-3 review R3-U13: each row's title opens THE POLICY ITSELF — its
 * published text, version and date in a drawer (`?policy=<key>`, so the
 * link is shareable), with Acknowledge when it's yours to acknowledge.
 *
 * Reads (any member): GET /policies/status, /policies, /policies/mine.
 * Writes: POST /policies (owner/admin), POST /policies/:id/acknowledge (self).
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { pendingPolicyKeys } from '../components/PendingPolicies';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, EmptyState, PageHeader, Spinner } from '@aigovops/design-system';
import { useWorkspace } from '../lib/workspace';
import { getPolicyAckStatus, listPublishedSoc2Policies } from '../lib/api';
import { SafeMarkdown } from '../components/SafeMarkdown';
import { fmtProgramDate } from '../lib/program-shell';
import { queryKeys } from '../lib/query-keys';
import { usePendingPolicies } from '../components/PendingPolicies';
import { AskAdmin } from '../components/AskAdmin';
import { AcknowledgeDrawer, PublishPolicyDrawer } from './Soc2Readiness';
import './programs.css';
import './today.css';
import './soc2-readiness.css';

/** The CMMC SSP as a policy (3.12.4): its key, title and a starter text. */
const SSP_PRESET = {
  key: 'ssp', title: 'System security plan', note: 'For CMMC 3.12.4',
  body: '# System security plan\n\nDescribe the system boundary, its environment and connections, and how each CMMC requirement is implemented.\n',
};

export function PoliciesPage() {
  const { workspace, role } = useWorkspace();
  const slug = workspace.slug;
  const canManage = role === 'owner' || role === 'admin';
  const [params, setParams] = useSearchParams();
  const openKey = params.get('policy');
  const wantsSsp = params.get('new') === 'ssp';
  const published = useQuery({ queryKey: pendingPolicyKeys.published(slug), queryFn: () => listPublishedSoc2Policies(slug), enabled: !!slug && (!!openKey || wantsSsp) });
  const shown = openKey ? published.data?.policies.find((x) => x.policy_key === openKey) ?? null : null;
  const closePolicy = () => { const next = new URLSearchParams(params); next.delete('policy'); setParams(next, { replace: true }); };
  const status = useQuery({ queryKey: queryKeys.policyAckStatus(slug), queryFn: () => getPolicyAckStatus(slug), enabled: !!slug });
  const { pending } = usePendingPolicies(slug);
  const [drawer, setDrawer] = useState<null | 'publish' | 'ack'>(null);
  const [notice, setNotice] = useState('');
  const opened = useRef(false);
  // PR A F15: the CMMC scorecard's SSP row opens the publish drawer for the system security plan (?new=ssp).
  // PR B r5: the requirement that sent us here ("For CMMC 3.8.9 · in the SSP (3.12.4)"); an SSP already published opens as its next version, with its current text.
  const forRef = (params.get('for') ?? '').match(/^[A-Za-z0-9.-]{1,24}$/)?.[0] ?? null;
  const sspNow = published.data?.policies.find((x) => x.policy_key === SSP_PRESET.key) ?? null;
  const preset = wantsSsp && !published.isLoading ? {
    ...SSP_PRESET,
    body: sspNow?.body_markdown ?? SSP_PRESET.body,
    note: forRef && forRef !== '3.12.4'
      ? `For CMMC ${forRef} · ${sspNow ? 'a new version of the SSP' : 'in the SSP'} (3.12.4)`
      : sspNow ? 'For CMMC 3.12.4 · a new version of the SSP' : SSP_PRESET.note,
  } : null;
  const presetOpened = useRef(false);
  // Kept after ?new= is cleared, so the drawer keeps its preset; a way back to where the link came from.
  const [presetHeld, setPresetHeld] = useState<typeof SSP_PRESET | null>(null);
  const ret = params.get('return');
  const backTo = ret && ret.startsWith(`/w/${slug}/app/`) ? ret : null;
  useEffect(() => {
    if (presetOpened.current || !preset || !canManage) return;
    presetOpened.current = true; setPresetHeld(preset); setDrawer('publish');
    // Clear ?new=ssp once opened (a reload doesn't reopen it).
    const next = new URLSearchParams(params); next.delete('new'); setParams(next, { replace: true });
  }, [preset, canManage, params, setParams]);
  useEffect(() => {
    if (opened.current || params.get('acknowledge') !== '1' || pending.length === 0) return;
    opened.current = true; setDrawer('ack');
  }, [params, pending.length]);

  // R4: a deep-linked drawer has no opener to return to — Esc / Cancel land on the way back (else the page title).
  const closePublish = () => {
    setDrawer(null);
    if (!presetHeld) return;
    window.setTimeout(() => {
      const el = document.querySelector<HTMLElement>('[data-part="policies-back"] a') ?? document.querySelector<HTMLElement>('.v96-soc2 h1');
      if (el && el.tagName === 'H1') el.tabIndex = -1;
      el?.focus();
    }, 30);
  };
  const policies = status.data?.policies ?? [];
  const pendingVersions = new Set(pending.map((p) => p.version_id));
  const prefix = `/w/${slug}/app`;
  const publish = canManage
    ? <Button variant={policies.length === 0 ? 'primary' : 'secondary'} onClick={() => setDrawer('publish')}>Publish a policy</Button>
    : <AskAdmin slug={slug} what="publishes policies" subject="GAC Pro: policy to publish" />;

  return (
    <div className="gp-programs v96-soc2">
      <PageHeader
        variant="app"
        title="Policies"
        right={<div className="gp-actions">
          {pending.length > 0 ? <Button variant="primary" onClick={() => setDrawer('ack')}>{`Acknowledge (${pending.length})`}</Button> : null}
          {policies.length > 0 ? publish : null}
        </div>}
      />
      {backTo ? <p className="v75-muted" data-part="policies-back"><Link to={backTo}>Back to the program</Link></p> : null}
      <div role="status" aria-live="polite" className={notice ? 'v96-soc2-notice' : 'sr-only'}>{notice}</div>
      {status.isLoading ? <Spinner label="Loading policies" />
        : status.error ? <Alert variant="error"><p>Policies could not load.</p><Button onClick={() => void status.refetch()}>Retry</Button></Alert>
        : policies.length === 0 ? <EmptyState title="No policies published" action={publish} />
        : (
          <table className="gp-table" role="table">
            <thead role="rowgroup"><tr role="row"><th role="columnheader" scope="col">Policy</th><th role="columnheader" scope="col">Status</th><th role="columnheader" scope="col">Acknowledged</th><th role="columnheader" scope="col"><span className="sr-only">Action</span></th></tr></thead>
            <tbody role="rowgroup">
              {policies.map((p) => {
                // U13: a status chip and a row action per policy — never a bare number table.
                const mine = pendingVersions.has(p.version_id);
                const done = p.members_required > 0 && p.acknowledged >= p.members_required;
                const left = Math.max(0, p.members_required - p.acknowledged);
                return (
                  <tr role="row" key={p.version_id} id={`policy-${p.policy_key}`}>
                    <td role="cell" data-label="Policy"><Link id={`policy-link-${p.policy_key}`} to={`${prefix}/policies?policy=${encodeURIComponent(p.policy_key)}`}>{p.title}</Link></td>
                    <td role="cell" data-label="Status"><span className={`gp-chip gp-chip--${mine ? 'amber' : done ? 'ok' : 'neutral'}`}>{mine ? 'Yours to acknowledge' : done ? 'Everyone acknowledged' : `${left} still to acknowledge`}</span></td>
                    <td role="cell" data-label="Acknowledged" className="gp-num">{`${p.acknowledged} of ${p.members_required} · ${Math.round(p.completion_pct)}%`}</td>
                    <td role="cell" className="gp-actions">
                      {mine ? <Button compact onClick={() => setDrawer('ack')} aria-label={`Acknowledge · ${p.title}`}>Acknowledge</Button>
                        : canManage ? <Button compact onClick={() => setDrawer('publish')} aria-label={`Publish a new version · ${p.title}`}>Publish a new version</Button>
                        : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      {openKey ? (
        <Drawer open modal onClose={closePolicy} label={shown ? shown.title : 'Policy'} className="v96-drawer v96-drawer-wide"
          returnFocus={() => document.getElementById(`policy-link-${openKey}`)}>
          <div className="v96-drawer-head"><h2 tabIndex={-1}>{shown ? shown.title : 'Policy'}</h2><Button variant="ghost" onClick={closePolicy} aria-label="Close">✕</Button></div>
          {published.isLoading ? <Spinner label="Loading the policy" />
            : !shown ? <EmptyState title="This policy isn’t published" action={<Button onClick={closePolicy}>Back to policies</Button>} />
            : <div className="v96-form" data-part="policy-text">
                <p className="v75-muted">{`Version ${shown.version}${shown.published_at ? ` · published ${fmtProgramDate(shown.published_at)}` : ''}`}</p>
                <div className="v96-policy-text" role="region" tabIndex={0} aria-label={`${shown.title} text`}><SafeMarkdown source={shown.body_markdown} /></div>
                {pendingVersions.has(shown.version_id)
                  ? <div className="v75-actions"><Button variant="primary" onClick={() => { closePolicy(); setDrawer('ack'); }}>Acknowledge</Button></div> : null}
              </div>}
        </Drawer>
      ) : null}
      {drawer === 'publish' && canManage && <PublishPolicyDrawer slug={slug} published={policies.map((p) => p.policy_key)} preset={presetHeld}
        onClose={closePublish} onPublished={(title) => { setDrawer(null); setNotice(`${title} published.`); }} />}
      {drawer === 'ack' && <AcknowledgeDrawer slug={slug} pending={pending} onClose={() => setDrawer(null)}
        onDone={(n) => { setDrawer(null); setNotice(`${n} polic${n === 1 ? 'y' : 'ies'} acknowledged.`); }} />}
    </div>
  );
}
